export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  icon: string;
}

export const faqData: FAQItem[] = [
  // Getting Started
  {
    id: "what-is-indiekeys",
    question: "What is IndieKeys?",
    answer: "IndieKeys is a platform that connects indie game developers with content creators. Developers can distribute promotional game keys to creators, while creators can request keys for games they want to cover on their channels. It's designed to help indie games get more visibility through content creation.",
    category: "Getting Started",
    icon: "Gamepad2"
  },
  {
    id: "account-types",
    question: "What types of accounts are available?",
    answer: "There are three main account types: Player accounts for gamers who want to earn coins and purchase games, Creator accounts for content creators who want to request game keys, and Developer accounts for indie game studios who want to distribute keys. Each type has access to different features and dashboards tailored to their needs.",
    category: "Getting Started",
    icon: "Users"
  },

  // Getting Keys (Creators)
  {
    id: "request-keys",
    question: "How do I request game keys?",
    answer: "Browse available games on the Games page, click on any game you're interested in, and click the 'Request Key' button. Make sure your profile is complete with your channel information and social media links to increase your chances of approval.",
    category: "Getting Keys",
    icon: "Key"
  },
  {
    id: "key-timing",
    question: "How long does it take to get a key?",
    answer: "Response time varies by developer, but most respond within 2-7 days. You'll receive notifications when your request is approved or denied, and approved keys will appear in your Key Requests page.",
    category: "Getting Keys",
    icon: "Clock"
  },
  {
    id: "key-usage",
    question: "Can I sell or give away keys I've received?",
    answer: "No, keys are provided for content creation only. You must never sell keys, give them to others, or use them in giveaways unless explicitly granted for that purpose. Misuse may result in account suspension.",
    category: "Getting Keys",
    icon: "Shield"
  },

  // For Developers
  {
    id: "developer-setup",
    question: "How do I set up my developer account?",
    answer: "Create a developer account and complete your studio profile with company information, logo, and description. Add your games with details, screenshots, and upload your promotional keys. Set your key request policies and start receiving requests from creators.",
    category: "For Developers",
    icon: "Settings"
  },
  {
    id: "key-management",
    question: "How do I manage game keys?",
    answer: "Upload your game keys through the key management system. Set availability limits and track usage. The system automatically decrements available keys when they're assigned to creators. Use the 'Sync Keys' button to update counts based on actual unclaimed keys.",
    category: "For Developers",
    icon: "Key"
  },
  {
    id: "request-review",
    question: "How do I review key requests?",
    answer: "View incoming requests in your dashboard with creator profiles, channel statistics, and request details. Approve or decline requests with optional messages. Approved requests automatically assign available keys to creators.",
    category: "For Developers",
    icon: "Users"
  },

  {
    id: "messaging-system",
    question: "How does the messaging system work?",
    answer: "The platform includes a built-in messaging system that allows direct communication between creators and developers. You can send messages about key requests, discuss collaboration opportunities, or ask questions without sharing personal contact information.",
    category: "Getting Keys",
    icon: "MessageSquare"
  },
  {
    id: "trust-score",
    question: "What is the trust score system?",
    answer: "The trust score system helps developers identify reliable creators based on their past performance, content quality, and platform engagement. Higher trust scores indicate more trustworthy creators who are likely to create quality content from received keys.",
    category: "For Developers",
    icon: "Star"
  },

  // For Players
  {
    id: "player-gems-system",
    question: "How does the gems system work for players?",
    answer: "As a player, you can earn gems by completing daily missions like logging in, liking games, adding favorites, and visiting game pages. You can also earn gems through weekly missions and one-time achievements. Gems can be used to purchase games directly on the platform.",
    category: "For Players",
    icon: "Sparkles"
  },
  {
    id: "daily-missions",
    question: "What are daily missions and how do I complete them?",
    answer: "Daily missions are tasks you can complete each day to earn gems. These include logging in daily, liking games, adding games to your favorites, visiting game pages, and more. Missions reset daily and provide a steady way to earn gems for purchasing games.",
    category: "For Players",
    icon: "Target"
  },
  {
    id: "weekly-missions",
    question: "What are weekly missions?",
    answer: "Weekly missions are longer-term challenges that reset every week. These typically involve accumulating playtime, completing multiple daily missions, or achieving specific milestones. Weekly missions offer larger gem rewards compared to daily missions.",
    category: "For Players",
    icon: "Calendar"
  },
  {
    id: "purchasing-games",
    question: "How do I purchase games with gems?",
    answer: "Browse the Games page to find games available for purchase with gems. Each game shows its gem price. Click on a game you want to buy, then use the 'Purchase with Gems' button. The game will be added to your library once the transaction is complete.",
    category: "For Players",
    icon: "ShoppingCart"
  },
  {
    id: "steam-connection",
    question: "How do I connect my Steam account?",
    answer: "Go to your Settings page and navigate to the Steam connection section. You'll need to provide your Steam profile URL. Once connected, the platform can track your playtime and achievements from purchased games on Steam.",
    category: "Getting Started",
    icon: "Link"
  },
  {
    id: "steam-requirements",
    question: "I am logged with Steam but can't ask for a key",
    answer: "You need to be connected with Steam in order to ask for a key. Moreover, the following requirements must be met:<br /><br /><ul class='list-disc ml-5 space-y-1'><li>Your Steam level must be greater than <b>30</b></li><li>You must have at least <b>50 hours</b> of total playtime on your account</li><li>You must own at least <b>5 games</b></li><li>Your Steam profile must be <b>public</b> and not 'limited'</li><li>Your account must be at least <b>3 months old</b></li></ul><br />These measures help prevent botting and ensure keys go to active players.",
    category: "Getting Started",
    icon: "Shield"
  },
  {
    id: "playtime-tracking",
    question: "How does playtime tracking work?",
    answer: "When you connect your Steam account, the platform automatically tracks your playtime for all purchased games. Visit the 'Play Time' tab in your player profile to see detailed statistics including total playtime, playtime in the last 2 weeks, and your achievement progress for each game. Data is synced daily to keep your stats up to date.",
    category: "For Players",
    icon: "Clock"
  },
  {
    id: "achievements-badges",
    question: "How do achievements and badges work?",
    answer: "Badges are earned by completing various missions and achievements throughout the platform. Progress through multiple tiers (Rookie, Ongoing, Master) in categories like gaming, reviewing, testing, and community participation. Visit the 'Achievements' tab in your player profile to see all available badges and your progress.",
    category: "For Players",
    icon: "Trophy"
  },
  {
    id: "100-percent-completion",
    question: "How is a game considered 100% completed?",
    answer: "A game is considered 100% completed when you have both: (1) unlocked 100% of all achievements in the game, and (2) played the game for at least 2 hours (120 minutes). Both conditions must be met for a game to count toward your completion statistics and related badges.",
    category: "For Players",
    icon: "CheckCircle"
  },

  // Account & Profile
  {
    id: "profile-setup",
    question: "How do I set up my profile?",
    answer: "Complete your profile by adding your channel information, preferred genres, social media links, and a bio. For developers, include your studio information and company details. The more complete your profile, the better others can understand your content and make informed decisions.",
    category: "Account & Profile",
    icon: "Settings"
  },
  {
    id: "social-linking",
    question: "How do I connect my social media accounts?",
    answer: "Go to your Settings page and connect your YouTube, Twitch, TikTok, Instagram, Twitter, and other social media accounts. This allows others to see your channel statistics and content style. You can only link one channel per platform.",
    category: "Account & Profile",
    icon: "Gamepad2"
  },
  {
    id: "discord-role",
    question: "How do I get my role on the Discord server?",
    answer: "Once you have connected your Discord account in Settings, go to our Discord server. Click on the server name (top-left) > Linked Roles, and select IndieKeys to automatically claim your role based on your account type (Player, Creator, or Developer).",
    category: "Account & Profile",
    icon: "Shield"
  },

  // Technical Issues
  {
    id: "login-issues",
    question: "I'm having trouble logging in. What should I do?",
    answer: "Try resetting your password using the 'Forgot Password' link on the login page. If you're still having issues, contact support with your email address and a description of the problem.",
    category: "Technical Issues",
    icon: "HelpCircle"
  },
  {
    id: "key-not-working",
    question: "A key I received isn't working. What should I do?",
    answer: "First, double-check that you're redeeming the key on the correct platform (Steam, Epic Games, etc.) and in the right region. If the key still doesn't work, contact the developer directly through the platform's messaging system.",
    category: "Technical Issues",
    icon: "AlertTriangle"
  }
];

export const categories = [
  "Getting Started",
  "Getting Keys",
  "For Developers",
  "For Players",
  "Account & Profile",
  "Technical Issues"
];
